import { Box, makeStyles, useMediaQuery } from "@material-ui/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { EventHero } from "../../components/heroImage/EventHero";
import useEventMetadata from "../../hooks/useEventMetadata";
import { useLearnMoreOrRegisterButton } from "../../hooks/useLearnMoreOrRegisterButton";
import { theme } from "../../shared/theme";
import { getResultsUrl } from "../../utils/resultsHelpers";
import { PageTemplate } from "../home/PageTemplate";
import { useGetMasterEventMetadataQuery } from "../../api/reigniteApi";

import { useMissingData } from "./hooks/useMissingData";

const usePageContainerStyles = makeStyles(({ breakpoints, palette }) => ({
  container: {
    borderBottom: `1px solid ${palette.grey[100]}`,
    margin: '45px auto 0',
    maxWidth: '960px',
    width: '100%',

    [breakpoints.up('sm')]: {
      margin: '50px auto 0',
      paddingTop: '25px',
    },
  }
}))

type PageContainerProps = {
  azpEventId: string
  children: ReactNode
  eventId: number | null
  eventCourseId: number | null
  isEventLive: boolean
}

const PageContainer = ({ 
  azpEventId,
  children,
  eventId,
  eventCourseId,
  isEventLive,
}: PageContainerProps) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const styles = usePageContainerStyles()

  const { ctLiveRegLink } = useMissingData({ eventId })

  const {
    data: event,
    isLoading: isLoadingEventMetadata,
  } = useEventMetadata(eventId)

  const masterId = event?.masterId || null

  const { data: master } = useGetMasterEventMetadataQuery(masterId ?? skipToken)

  const { learnMoreRegisterButton } = useLearnMoreOrRegisterButton({
    ctLiveRegLink,
    eventId,
    learnMoreLink: event.websiteUrl || undefined,
    startTime: event?.eventStartDateTime?.timeInMillis || -1,
    thirdPartyRegLink: event?.thirdPartyRegLink || undefined,
  })

  const isLoadingEvent = !masterId || !eventId || isLoadingEventMetadata

  return (
    <PageTemplate
      unifiedSearchMode={true}
      headerIsFixed={true}
      isMobile={isMobile}
      paddingTop={0}
      bannerTopOffset={50}
      t={t}
    >
      <Box className={styles.container}>
        <EventHero
          actionButton={learnMoreRegisterButton}
          ctliveEventId={azpEventId}
          eventId={eventId ?? -1}
          eventLogoUrl={event.logoUrl}
          eventType={event.eventType}
          isCustomHeroImage={event.hasHeroImage}
          isLoading={isLoadingEvent}
          isRosterTabAvailable={event.isRosterAvailable}
          isLiveRaceHeaderVisible={isEventLive}
          liveEventURL={(eventId && masterId) ? getResultsUrl(masterId, eventId) : ''}
          masterId={masterId}
          masterName={master?.name || ''}
          raceId={eventCourseId}
        />

        {children}
      </Box>
    </PageTemplate>
  )
}

export { PageContainer };